import { useTranslation } from "react-i18next";
import type { AdminUserContactLabels } from "./AdminUserContactCard";
import type { AdminUserDetailLabels } from "./types";
import type { CreateAdminUserModalLabels } from "./CreateAdminUserModal";

type RoleNamespace = "sellers" | "hosts" | "salesAgent";

export function useAdminUserDetailLabels(options: {
  roleLabel: string;
  statRelevant: string;
  loadDetail: string;
  loadError: string;
}): AdminUserDetailLabels {
  const { t } = useTranslation("adminUsers");

  return {
    back: t("detail.backToUsers"),
    roleLabel: options.roleLabel,
    activate: t("detail.activate"),
    deactivate: t("detail.deactivate"),
    statWallet: t("detail.statWallet"),
    statRevenue: t("detail.statRevenue"),
    statRelevant: options.statRelevant,
    transactionsTitle: t("detail.transactionsTitle"),
    loadDetail: options.loadDetail,
    loadError: options.loadError,
    statusActive: t("detail.statusActive"),
    statusInactive: t("detail.statusInactive"),
    editDisplayName: t("detail.editDisplayName"),
    displayNamePlaceholder: t("detail.displayNamePlaceholder"),
    displayNameEmpty: t("detail.displayNameEmpty"),
    toastDisplayNameUpdated: t("detail.toastDisplayNameUpdated"),
    toastDisplayNameError: t("detail.toastDisplayNameError"),
    impersonate: t("impersonate"),
  };
}

export function useAdminUserContactLabels(
  roleNs: RoleNamespace,
  options?: { toastPasswordResetSent?: string },
): AdminUserContactLabels {
  const { t } = useTranslation("adminUsers");
  const { t: roleT } = useTranslation(roleNs);

  return {
    contactCard: t("detail.contactCard"),
    email: t("detail.email"),
    sendPasswordReset: t("detail.sendPasswordReset"),
    toastPasswordResetSent:
      options?.toastPasswordResetSent ??
      roleT("detail.toastPasswordResetSent", {
        defaultValue: t("detail.toastPasswordResetSent"),
      }),
    passwordResetActiveLink: t("detail.passwordResetActiveLink"),
    passwordResetCooldown: t("detail.passwordResetCooldown"),
    phone: t("detail.phone"),
    contactEmail: t("detail.contactEmail"),
    vatNumber: t("detail.vatNumber"),
    billingAddress: t("detail.billingAddress"),
  };
}

export function useAdminUserCreateModalLabels(options: {
  title: string;
  namePlaceholder: string;
  emailPlaceholder?: string;
  create: string;
}): CreateAdminUserModalLabels {
  const { t } = useTranslation("adminUsers");
  const { t: tCommon } = useTranslation("common");

  return {
    title: options.title,
    nameLabel: t("createModal.nameLabel"),
    namePlaceholder: options.namePlaceholder,
    regionLabel: t("createModal.regionLabel"),
    regionPlaceholder: t("createModal.chooseRegion"),
    regionRequired: t("createModal.regionRequired"),
    emailLabel: t("createModal.emailLabel"),
    emailPlaceholder: options.emailPlaceholder ?? "",
    phoneLabel: t("createModal.phoneLabel"),
    phonePlaceholder: t("createModal.phonePlaceholder"),
    phoneRequired: t("createModal.phoneRequired"),
    inviteEmailHint: t("createModal.inviteEmailHint"),
    cancel: tCommon("cancel"),
    create: options.create,
  };
}
